// SETTINGS V2 — phone section-selector trigger field (SETTINGS_V2_SP1 §6).
//
// Port of designer SPhoneSelector (settings-responsive.jsx lines 110-123) as a
// REAL <button type="button"> — the designer div is not keyboard-usable; a
// native button gives Enter/Space activation for free. Opens the phone bottom
// sheet by dispatching setPhoneNavOpen(true); ALL close paths live in the sheet.
//
// Shows the active section's glyph + translated label ONLY — NO email, NO user
// name anywhere (carried SP0 rule). Active item resolved via the shared
// isSettingsV2SectionActive helper (§4.2), falling back to the `profile` item.
//
// Styling stack: inline styles + scoped CSS-var tokens. No MUI, no sx, no dt,
// no Tailwind. No new i18n keys (renders existing nav labels).
import React from "react";
import { useLocation } from "react-router-dom";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import useSettingsV2Nav, { isSettingsV2SectionActive } from "../useSettingsV2Nav";
import { setPhoneNavOpen } from "../settingsV2UiSlice";
import settingsV2NavGlyphs from "./settingsV2NavGlyphs";

// Static chevron-down (no rotation — designer parity).
const ChevronDown: React.FC = () => (
  <svg
    viewBox="0 0 12 12"
    width={13}
    height={13}
    fill="none"
    stroke="var(--muted)"
    strokeWidth={1.5}
    strokeLinecap="round"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <path d="M3 4.5 L6 7.5 L9 4.5" />
  </svg>
);

const SettingsV2SectionSelector: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { translate } = useTranslation("settings");
  const { groups } = useSettingsV2Nav();
  const open = useAppSelector((s) => s.settingsV2Ui.phoneNavOpen);

  const flat = groups.flatMap((g) => g.items);
  const activeItem =
    flat.find((it) => isSettingsV2SectionActive(location.pathname, it.path)) ||
    flat.find((it) => it.path === "profile") ||
    flat[0];

  const Glyph = activeItem ? settingsV2NavGlyphs[activeItem.key] : undefined;

  return (
    <button
      type="button"
      onClick={() => dispatch(setPhoneNavOpen(true))}
      aria-expanded={open}
      aria-haspopup="dialog"
      aria-controls="sv2-phone-nav-sheet"
      style={{
        width: "100%",
        height: 46,
        padding: "0 13px",
        borderRadius: 10,
        display: "flex",
        alignItems: "center",
        gap: 10,
        background: "var(--card)",
        border: `1px solid ${open ? "var(--mod-accent)" : "var(--border)"}`,
        boxShadow: open ? "0 0 0 3px var(--mod-accent-soft)" : "var(--shadow)",
        cursor: "pointer",
        fontFamily: "inherit",
        textAlign: "left",
      }}
    >
      <span
        style={{ color: "var(--mod-accent)", display: "inline-flex", flexShrink: 0 }}
      >
        {Glyph && <Glyph />}
      </span>
      <span
        style={{
          flex: 1,
          fontSize: 14,
          fontWeight: 600,
          color: "var(--ink)",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {activeItem ? translate(activeItem.labelKey) : ""}
      </span>
      <ChevronDown />
    </button>
  );
};

export default SettingsV2SectionSelector;
